import { CodeHighlight } from '@mantine/code-highlight';
import { Alert, Card, Code, Flex, Paper, SimpleGrid, Text, Title } from '@mantine/core';
import { useMemo } from 'react';
import { NavLink } from 'react-router';
import { ExternalLink } from '../../components/ExternalLink';
import { PageWrapper } from '../../components/PageWrapper/PageWrapper';
import { useCalculators } from '../../hooks/use-calculators';
import { hasNpmPackage } from '../../versions/npm-package';
import { usesSplitOpenApiSpecs } from '../../versions/split-openapi';
import { useOpenAPISpec } from '../../versions/use-openapi-spec';
import { useVersion } from '../../versions/url-version';
import { apiSlug } from '../../versions/versions';

export function Welcome() {
  const version = useVersion();
  const apiVersion = apiSlug(version);

  // Split versions list their calculators in a manifest, legacy versions have every path in one openapi.json
  const splitSpecs = usesSplitOpenApiSpecs(version);
  const { selectedSpec } = useOpenAPISpec(splitSpecs ? undefined : version);
  const { calculators: manifest } = useCalculators(splitSpecs ? version : undefined);

  const calculators = useMemo(() => {
    const paths = splitSpecs
      ? (manifest ?? []).map((calculator) => `/${calculator}`)
      : Object.keys(selectedSpec?.paths || {});
    return paths.sort((a, b) => a.localeCompare(b));
  }, [splitSpecs, manifest, selectedSpec]);

  return (
    <PageWrapper>
      <h2>👋 Welcome!</h2>
      <p>
        Welcome to the AIA Emissions Calculators documentation. Here you'll find everything you need to get started with
        the calculators, including authentication information, endpoints, and contact information to help you through.
      </p>
      <h2>What are the AIA Emissions Calculators?</h2>
      <p>
        The AIA Emissions Calculators are a set of APIs that allow you to calculate the carbon emissions of various
        agricultural activities. The APIs are designed to be easy to use and integrate into your existing systems. You
        can perform calculations for a range of livestock, cropping and other production activities.
      </p>
      {hasNpmPackage(version) && (
        <Alert variant="light" color="blue" mb="lg">
          🔎 The calculators are available as a REST API endpoint or as an NPM package. To find out more about consuming
          the NPM package, jump straight to the <NavLink to="../package">NPM Package</NavLink> documentation. Otherwise
          continue on here to find out about the REST API endpoints.
        </Alert>
      )}
      <p>The calculators currently available are:</p>
      <SimpleGrid cols={2}>
        {calculators.map((calculator) => (
          <Card withBorder bg="var(--color-bg-secondary)" key={calculator}>
            {calculator}
          </Card>
        ))}
      </SimpleGrid>
      <br />
      <Title order={2}>REST API</Title>
      <br />
      <Paper withBorder p="md" mb="md">
        <Text size="sm" fw={500} mb="xs">
          An example URL for accessing the beef calculator is:
        </Text>
        <Code block>{__API_URL__}/calculator/latest/beef</Code>
        <Text size="sm" fw={500} mb="xs">
          <ul>
            <li>
              Replace <code>latest</code> with the version number you want to use, for example <code>{apiVersion}</code>
            </li>
            <li>
              Replace <code>beef</code> with the calculator name you want to use, for example <code>grains</code>
            </li>
          </ul>
        </Text>
      </Paper>
      <Title order={3}>Versioning</Title>
      <p>
        The AIA Emissions Calculators API is strictly versioned. Every time the API is updated, a new version is
        released. The versioning strategy uses{' '}
        <ExternalLink href="https://semver.org/">Semantic Versioning</ExternalLink> to help you deal with breaking
        changes and feature enhancements.
        <br />
        <br />
        You can specify the version of the API you want to use in your requests, guaranteeing that if you always use the
        same version, your code or request payload will continue to work as expected, and you'll get the same results
        you received the last time you ran the calculation. On each endpoint, the version is indicated in the URL.
        Here's an example:
        <br />
        <br />
        <Code block>
          {__API_URL__}/calculator/{apiVersion}/beef
        </Code>
        <br />
        In this example the version number is <code>{apiVersion}</code>. You can also specify a version alias that will
        be matched dynamically. The version alias <code>latest</code> can be used, which currently matches to version{' '}
        <code>{apiSlug(__API_LATEST_ALIAS__)}</code>.
      </p>
      <h2>Show me a request</h2>
      <p>
        If you want to dive straight in to performing a calculation, an example of a basic request to the beef
        calculator using the cURL command line tool is shown here. Note that this won't succeed until you have a valid
        certificate and private key:
      </p>
      <Paper withBorder p="md" mb="md">
        <Text size="sm" fw={500} mb="xs">
          Example cURL Request
        </Text>
        <CodeHighlight
          language="bash"
          code={`
          curl --request POST \\
          --key /path/to/your/client-request.key \\
          --cert /path/to/your/client-request.pem \\
          --url https://emissionscalculator-mtls.production.aiaapi.com/calculator/2.0.0/beef \\
          --header 'Accept: application/json' \\
          --header 'Content-Type: application/json' \\
          --header 'User-Agent: my-organisation-name-integration' \\
          --data '{'{ ... valid request payload ... }'}'
        `}
        />
      </Paper>
      <Title order={3}>More examples</Title>
      <p>
        If you visit the the <NavLink to="../api-reference">REST API Reference</NavLink> page, there are valid example
        requests for each calculator. You can try modifying the request payload to see how the results change. And you
        can export the example request in many popular programming and scripting languages.
      </p>

      <p>
        There's also a complete set of working example clients available in the{' '}
        <ExternalLink href="https://github.com/aginnovationaustralia/emissions-calculators-examples">
          emissions-calculators-examples
        </ExternalLink>
        repository. These examples are ready to use and show how to make requests to the API. All you will need is your
        valid certificate files, and you'll be able to make requests straight away.
      </p>
      <p>
        You can find examples in popular languages like C#, PHP, Python and TypeScript. The client code includes classes
        and types for all the entities used by all the calculators. They have been code generated for you using the
        OpenAPI spec file for the API. The scripts used for the code generation are also available to use. This is a
        great way to get started with the API and see how to make requests straight away.
      </p>
      <Title order={3}>Next steps</Title>
      <p>
        As the API uses mutual TLS security, you will need valid client certificate files to make a request. Once you
        have received the response to your certificate request, you can explore the API reference to see how to make
        requests to the API. The API reference will help you to create a valid request payload, and allows you to make a
        request straight away in the browser:
      </p>
      <Flex justify="space-between" align="stretch" gap="md" w="100%">
        <Card withBorder bg="var(--color-bg-secondary)" style={{ flex: 1 }}>
          <NavLink to="../authentication">Authentication and Certificates</NavLink>
        </Card>
        <Card withBorder bg="var(--color-bg-secondary)" style={{ flex: 1 }}>
          <NavLink to="../api-reference">API Reference</NavLink>
        </Card>
      </Flex>
      <h2>Need help?</h2>
      <p>
        Send us an email at{' '}
        <a href="mailto:contact@aginnovationaustralia.com.au">contact@aginnovationaustralia.com.au</a> and we'll help
        out!
      </p>
    </PageWrapper>
  );
}
